import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Calendar, FileText, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyRegistreFiscalPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/registre-fiscal/verify/${token}`);
        if (!cancel) setState({ loading: false, data: res.data, error: null });
      } catch (err) {
        if (!cancel) setState({ loading: false, data: null, error: err?.response?.data?.detail || 'Erreur' });
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-8" data-testid="verify-registre-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 shadow-md">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Vérification du Registre Fiscal</h1>
            <p className="text-xs text-slate-500">OBJECTIF SCOP OUTREMER — Authentification cryptographique SHA-256</p>
          </div>
        </div>

        {state.loading && (
          <div className="py-10 text-center text-slate-500">Vérification en cours…</div>
        )}

        {state.error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <XCircle className="w-7 h-7 text-red-500" />
              <div className="font-bold text-red-700">Signature invalide</div>
            </div>
            <div className="text-sm text-red-600">{state.error}</div>
            <div className="text-xs text-red-500 mt-3 font-mono">Token : {token}</div>
          </div>
        )}

        {state.data && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                <div>
                  <div className="font-bold text-emerald-800">Document authentique</div>
                  <div className="text-xs text-emerald-600">Émis par OSO et signé numériquement</div>
                </div>
              </div>
              <div className="text-xs text-emerald-700 mt-2 font-mono">Signature : {state.data.signature_short}</div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <Calendar className="w-3.5 h-3.5" /> Émis le
                </div>
                <div className="font-medium text-slate-800">
                  {state.data.generated_at ? new Date(state.data.generated_at).toLocaleString('fr-FR') : '—'}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                  <FileText className="w-3.5 h-3.5" /> Période
                </div>
                <div className="font-medium text-slate-800">
                  {state.data.period?.start || '—'} → {state.data.period?.end || '—'}
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Totaux</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-emerald-50">
                  <div className="text-xs text-emerald-600">Total HT taxé</div>
                  <div className="font-bold text-emerald-800 text-lg tabular-nums">
                    {Number(state.data.totals?.ht_global || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-amber-50">
                  <div className="text-xs text-amber-600">Taxes collectées</div>
                  <div className="font-bold text-amber-800 text-lg tabular-nums">
                    {Number(state.data.totals?.taxe_global || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-blue-50">
                  <div className="text-xs text-blue-600">Documents fiscalisés</div>
                  <div className="font-bold text-blue-800 text-lg">{state.data.totals?.nb_documents || 0}</div>
                </div>
                <div className="p-3 rounded-lg bg-purple-50">
                  <div className="text-xs text-purple-600">Lignes du registre</div>
                  <div className="font-bold text-purple-800 text-lg">{state.data.totals?.nb_rows || 0}</div>
                </div>
              </div>
            </div>

            <div className="text-[10px] text-slate-400 break-all border-t pt-3 font-mono">Token : {token}</div>
          </div>
        )}
      </div>
    </div>
  );
}
